// WorkspacePage.jsx — every video you have made, newest first, with a composer docked below.
//
// A GRID, not a column: the point of a workspace is seeing your work at a glance, and one item
// per row meant two fitted on screen. Grouped by age rather than paginated, so scrolling back
// is scrolling back in TIME.
//
// Clicking a card opens the viewer rather than navigating, so the grid keeps its scroll
// position and you can move through several with the arrow keys without losing your place.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Film, ArrowUp, Layers,
  FileText, GraduationCap, Sparkles,
  AlertTriangle, X, RotateCcw,
} from "lucide-react";
import { apiService } from "../apiService";
import { failureReasonFor } from "../utils/constants";
import VideoThumbnail from "../components/VideoThumbnail";
import ProgressDial from "../components/ProgressDial";
import { GeneratingBubble } from "../components/OnboardingBubbles";
import { displayTitle } from "../utils/helpers";
import { afterViewTransition } from "../utils/viewTransition";
import {
  DOCK_THIN_MS, DOCK_SLIDE_MS, DOCK_THIN_EASE, DOCK_SLIDE_EASE, takeComposerFrom,
} from "../utils/composerHandoff";
import { renderLatex } from "../study/renderLatex";
import {
  ROW_TITLE_FONT,
  safeRenderLatex,
  CLAMP_2_LINES,
  TITLE_SCRIM,
} from "../utils/videoTitle";
import CreateProjectModal from "../projects/CreateProjectModal";
import { listHomeworkSessions, updateHomeworkSession } from "../homework/homeworkApi";
import { listLectureSessions, updateLectureSession } from "../lecture/lectureApi";
import { useT } from "../i18n/useT";
import { cardLinkProps } from "../utils/cardLink";
import { takePendingUpload } from "../projects/pendingUpload";
import { useIsPhone } from "../hooks/useIsPhone";
import { useHoverPreview } from "../components/useHoverPreview";
import { COUNTDOWN_TOTAL_VIDEO, COUNTDOWN_TOTAL_OTHER } from "../components/GenerationQueue";

// Modes and the symbol palette are shared with the Home composer — see composerConfig.js
// for why they cannot live as a literal on each page.
import { COMPOSER_MODES as MODES, DEV_EMPTY_MODE, DEV_FAIL_MODE, resolveMode } from "../components/composerConfig";
import PromptComposer from "../components/PromptComposer";

/* Projects (homework / lecture / self-study) sit in the SAME day rows as videos rather
   than in a band of their own. A project and the videos it produced were made in one
   sitting, so splitting them into two places makes one afternoon read as two unrelated
   events. Icons match the composer's mode picker on purpose. */
const PROJECT_CONFIG = {
  hw:         { Icon: FileText,      label: "Homework", bg: "#EEF2FF", fg: "#6366F1" },
  lecture:    { Icon: GraduationCap, label: "Lecture",  bg: "#FAF5FF", fg: "#A855F7" },
  self_study: { Icon: Sparkles,      label: "Project",  bg: "#F0FDFA", fg: "#14B8A6" },
};

/* One line of preview text out of an item, whatever shape it arrived in: homework problems
   are plain strings, lecture topics are {title, description}. */
// Pulls the display text off a problem/topic, leaving the math INTACT for KaTeX.
//
// This used to strip the $…$ delimiters and the \( \) forms, on the theory that a preview
// wants plain text. That is backwards: removing the delimiters does not remove the LaTeX
// inside them, it just stops anything from rendering it — so covers showed "\vec{a}" and
// "3 \times 10^8" as literal backslashes. The delimiters are what safeRenderLatex needs.
const ROW_INITIAL = 10;
const ROW_STEP = 10;

/* A day row renders only its first ROW_INITIAL cards; the rest arrive as you scroll that
   row to the right.

   Every card in every row used to mount immediately, thumbnail <img> and all: 82 cards and
   60 thumbnails on first paint, for a page that shows about three cards per row. A busy day
   holds 49 of them, nearly all parked off the right edge of a row nobody ever scrolled.
   Thumbnails are only ~31 KB each, so this is about request count and decode work rather
   than bandwidth — measured, after an earlier estimate wrongly charged HomePage's ~14 MB of
   images to this page (both pages are mounted at once). The <img> tags already carry
   loading="lazy", which does nothing here: the browser only defers images outside the
   VIEWPORT, and a horizontally-overflowing card at x=6000 inside a visible row counts as
   in-viewport to that heuristic. Not rendering the card at all is what actually defers it. */
const DayRow = ({ total, phone, children }) => {
  const [count, setCount] = useState(ROW_INITIAL);
  const scrollerRef = useRef(null);
  const sentinelRef = useRef(null);
  const hasMore = count < total;

  useEffect(() => {
    if (!hasMore) return;
    const root = scrollerRef.current;
    const el = sentinelRef.current;
    if (!root || !el) return;
    // root is the ROW, not the document, and the margin is on the RIGHT edge — this is a
    // horizontal scroller, so a normal vertical rootMargin would never fire. Re-created on
    // every `count` change so it re-checks immediately: if the sentinel is still within the
    // margin after a batch, the next one loads without waiting for another scroll event.
    // The margin points along the axis the row scrolls — horizontal at every width. It was
    // briefly vertical on phones, for a wrapped grid that no longer exists.
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setCount((c) => c + ROW_STEP);
      },
      { root, rootMargin: "0px 800px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, count, phone]);

  return (
    <div
      ref={scrollerRef}
      /* A phone row is a horizontal scroller sized EXACTLY like Explore's: 6px gaps and
         `calc((100% - 2*6px)/3)` floored at 200px, so a card is 200px and a row shows about
         one and a half of them.
         It passed through a 2-up grid on the way here. That beat the original — cards were
         391px, the full width of the screen — but a grid cell is 174px, narrower than
         Explore's card, and its 14px title had to shrink to 11px to fit. Same numbers as
         Explore now, so the two pages finally read as one product. */
      className={phone
        ? "flex gap-[6px] overflow-x-auto ws-row"
        : "flex gap-4 overflow-x-auto -mx-1 px-1 ws-row"}
      style={phone ? { scrollbarWidth: "none" } : undefined}
    >
      {children(count)}
      {hasMore && (
        <div ref={sentinelRef} aria-hidden="true" className="flex-shrink-0" style={{ width: 1 }} />
      )}
    </div>
  );
};

/* VERTICAL deferral, to DayRow's horizontal.
 *
 * DayRow stops a busy day from mounting 49 cards, but every day row still mounted its first
 * ROW_INITIAL the moment the page did. On a full Workspace that is ~15 rows x 10 cards = ~150
 * cards and their thumbnails on first paint; measured on the live page, 196 <img> elements and
 * 107 thumbnail requests, p50 445ms and p90 6.0s each.
 *
 * loading="lazy" does not cover this, for the same reason it does not cover DayRow's case: the
 * browser's deferral margin is generous, so rows well below the fold fetch anyway. Not
 * rendering the row is what actually defers it.
 *
 * The day HEADER always renders — it is a line of text, it costs nothing, and keeping it means
 * the page still reads as a record of when you worked while you scroll. Only the row of cards
 * waits.
 *
 * minHeight RESERVES the row's space until it mounts, or every row that appeared would shove
 * the content below it downward and fight the scroll you are already doing. Once shown it stays
 * shown: re-hiding on scroll-away would re-request the thumbnails it just fetched.
 */
const RowGate = ({ eager, phone, children }) => {
  const [show, setShow] = useState(eager);
  const ref = useRef(null);

  useEffect(() => {
    if (show) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShow(true);
          io.disconnect();
        }
      },
      // Vertical margin here, unlike DayRow's horizontal one — this observer watches the
      // page scroll. 600px is roughly two rows of lead time, enough that a row is mounted
      // and its thumbnails requested before it reaches the fold.
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);

  return (
    <div ref={ref} style={show ? undefined : { minHeight: phone ? 140 : ROW_H }}>
      {show ? children : null}
    </div>
  );
};

/* One lesson card. Its own component because it holds hover-preview state, and the cards
   are produced inside a .map — hooks cannot live in a loop body.

   The preview sits ABOVE the thumbnail but BELOW the scrim and title: VideoThumbnail's
   <img> is `relative z-20`, so a z-20 sibling rendered after it paints on top, while the
   scrim and title at z-30 stay legible over whichever of the two is showing. */
const WsVideoCard = ({ v, href, onOpen, t, phone }) => {
  const { hoverProps, preview } = useHoverPreview(
    v.download_url,
    "absolute inset-0 z-20 w-full h-full object-cover transition-opacity duration-300",
  );
  return (
    // An <a> with a real href, not a <button>: right-click had no "Open in New Tab",
    // Cmd-click did nothing, and hovering showed no URL, because a button is not a link as
    // far as the browser is concerned. cardLinkProps keeps the fast in-page viewer on a
    // plain click and hands modified clicks back to the browser.
    <a
      {...cardLinkProps(href, onOpen)}
      {...hoverProps}
      style={phone ? { width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 } : { width: CARD_W, height: ROW_H }}
      className={`group relative shrink-0 rounded-xl overflow-hidden bg-black text-left border border-gray-200 hover:border-[#635BFF] transition-colors ${
        phone ? "aspect-video" : ""
      }`}
    >
      <VideoThumbnail video={v} alt={v.title || ""} loading="lazy" />
      {preview}
      {/* Title on the art, matching Explore and the home row. The Workspace card carried NO
          label at all before — the day header names a busy day only by its count, so an
          individual clip was unidentifiable without opening it. */}
      <div
        className="absolute inset-0 z-30 pointer-events-none"
        style={{ background: TITLE_SCRIM }}
      />
      {/* Padding on the WRAPPER, never on the clamped <p> — see CLAMP_2_LINES. With p-3
          directly on the clamped element a third line painted into the padding, sliced
          through the middle of the glyphs. */}
      <div className="absolute bottom-0 left-0 right-0 z-30 p-2.5 pointer-events-none">
        <p
          className="text-white text-sm sm:text-base font-bold leading-snug drop-shadow [&_.katex]:text-[0.9em]"
          style={{ fontFamily: ROW_TITLE_FONT, ...CLAMP_2_LINES }}
          dangerouslySetInnerHTML={{
            __html: safeRenderLatex(displayTitle(v.title) || v.prompt || t("ws_untitled")),
          }}
        />
      </div>
    </a>
  );
};

/* Full-text LaTeX for the prompt panel. Falls back to escaped plain text so a malformed
   expression shows the prompt rather than blanking it. */
const renderPromptLatex = (text) => {
  try {
    return renderLatex(String(text));
  } catch {
    return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
};

const previewLabel = (item) => {
  const raw = typeof item === "string" ? item : (item?.title || item?.text || item?.name || "");
  return String(raw).replace(/\s+/g, " ").trim();
};

/* ONE card size for everything, like the reference — a row of identical boxes, not a
   ragged mix. Sizing each card to its own aspect ratio was wrong twice over: it made the
   row ragged, and every thumbnail we store is a 16:9 canvas (a reel is letterboxed INTO
   that canvas, it is not a 9:16 image), so a 9:16 box just centre-cropped the frame into
   a sliver. Height 220 is taken from the reference's proportions; at 16:9 that keeps
   three cards on screen at 1440. */
const ROW_H = 220;
const CARD_W = Math.round(ROW_H * 16 / 9);



const dayKey = (iso) => {
  if (!iso) return "unknown";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "unknown"
    : `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

/** "Today", "Yesterday", else the date. Only days that actually have work get a row. */
const dayLabel = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(d, today)) return "Today";
  if (same(d, y)) return "Yesterday";
  return d.toLocaleDateString(undefined,
    { weekday: "long", month: "short", day: "numeric" });
};



const WorkspacePage = ({ user, credits, creditsLoading, generateUnified, generationQueue, isVisible = true }) => {
  const t = useT();
  const navigate = useNavigate();
  // Workspace search filters YOUR videos in place (the nav field routes here with ?q= when
  // you search from this page). Read from the URL rather than local state so the query
  // survives a reload and is shareable, matching how Explore does it.
  const wsSearch = useLocation().search;
  const wsQuery = (new URLSearchParams(wsSearch).get("q") || "").trim();
  const [videos, setVideos] = useState([]);
  // Homework / lecture / self-study sessions. These used to live on the Study page; with
  // that page gone they belong here or they are unreachable except by direct URL.
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [prompt, setPrompt] = useState("");
  const [mode, setMode] = useState("mode_fast_37");

  // Phones render the day rows as a 2-up grid instead of a horizontal scroller — see DayRow.
  const phone = useIsPhone();

  // `?mode=` preselects the composer so a shortcut elsewhere can land you here already set to
  // Homework, Lecture or Project.
  //
  // An EFFECT keyed on the search string, not a useState initializer: this page is
  // always-mounted, so an initializer runs once on the app's first render and would never
  // fire for a visitor arriving from Home. Keying on the search string also means it applies
  // on navigation only — it will not fight you when you change the mode by hand afterwards.
  //
  // Validated against MODES rather than trusted: an unknown value would leave `activeMode`
  // falling back to Fast while the composer believed it was in some other mode.
  useEffect(() => {
    const params = new URLSearchParams(wsSearch);
    const want = params.get("mode");
    // resolveMode, not MODES.find: hw and lecture are valid URLs that the picker no longer
    // lists, and searching the picker alone would silently drop the Home shortcut cards onto
    // a default composer instead of their uploader.
    const wanted = resolveMode(want);
    const seed = params.get("text") || "";
    /* THE PARAM IS AN INSTRUCTION, AND AN INSTRUCTION IS OBEYED ONCE. `?mode=hw` means "open
       the homework uploader", and it used to stay in the address bar for the whole life of
       the modal — so the history entry you came from was `/workspace?mode=hw`, and coming
       BACK to it ran this effect again and put the uploader straight back on screen. Leaving
       a homework set you had just made therefore landed you on "Drop your homework here",
       with no way to reach the Workspace except pressing back twice.
       Replaced, not pushed: the entry is rewritten in place, so Back goes where it would
       have gone anyway rather than to a URL that re-opens the dialog. `q` and anything else
       on the query string are kept — only the two params consumed here are dropped. */
    const consume = () => {
      const rest = new URLSearchParams(wsSearch);
      rest.delete("mode");
      rest.delete("text");
      const qs = rest.toString();
      navigate(`/workspace${qs ? `?${qs}` : ""}`, { replace: true });
    };
    if (wanted?.input === "handoff") {
      // Same reasoning as the picker above: a shortcut that says "Homework PDF" should land
      // you on the uploader, not on a text box that hides it behind an Enter press.
      setCreateSeed({ type: wanted.project, topic: seed.trim(), upload: wanted.upload });
      consume();
      return;
    }
    // ?mode=upload — a PDF attached on Home. The file travelled in module memory (see
    // projects/pendingUpload.js), so collect it and open the chooser HERE, which is where
    // the project it makes is going to live. No type: the modal asks, exactly as it did when
    // Home opened it. `files` may be null if the page was reloaded on this URL, and that is
    // fine — the modal's own uploader is the first thing in it.
    if (want === "upload") {
      // `upload: true` regardless of whether a file actually arrived. The file travels in
      // module memory, so a RELOAD of this URL — or someone opening it directly — lands here
      // with nothing, and without this flag the chooser falls back to all four types, two of
      // which cannot read a document and now have no art. The route itself is the intent:
      // ?mode=upload means "I am starting from a file", whether or not the file survived.
      setCreateSeed({ files: takePendingUpload(), topic: seed.trim(), upload: true });
      consume();
      return;
    }
    if (wanted) setMode(want);
    // `?text=` carries whatever was already typed into the Home composer, so the sentence
    // you had just written does not vanish on the way over. NOT `?q=` — that is this page's
    // search box, and seeding the composer from it would mean searching your library also
    // rewrote the prompt.
    if (seed) setPrompt(seed);
  }, [wsSearch, navigate]);
  const [sending, setSending] = useState(false);
  // The create flow runs on this page. It used to navigate to Study and open there, which
  // meant submitting from the Workspace bounced you out of the Workspace.
  const [createSeed, setCreateSeed] = useState(null);

  // Paged, not a single 200-video wall: a fixed cap meant the grid just stopped a few days
  // back with no way to reach anything older.
  const PAGE = 60;
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const offsetRef = useRef(0);

  const fetchPage = useCallback(async (offset) => {
    const data = await apiService.getUserContent(user.uid, "videos", PAGE, offset);
    const page = (data?.videos || []).filter((v) => v.video_id);
    return { page, done: page.length < PAGE };
  }, [user?.uid]);

  // Sessions are a single small fetch (no pagination) and are deliberately NOT tied to
  // the video pager: a project made months ago should still appear in its own day row even
  // though its videos are many pages down.
  const loadProjects = useCallback(async () => {
    if (!user?.uid) return;
    const [hw, lec] = await Promise.all([
      listHomeworkSessions().catch(() => ({ sessions: [] })),
      listLectureSessions().catch(() => ({ sessions: [] })),
    ]);
    const out = [
      ...(hw.sessions || []).map((x) => ({ ...x, _kind: "project", _type: "hw",
                                           _collection: "homework" })),
      // project_type "video" is a single-video wrapper whose video ALREADY appears in the
      // timeline from video_history. Including the session too would show the same thing
      // twice in the same row.
      ...(lec.sessions || [])
        .filter((x) => x.project_type !== "video")
        .map((x) => ({ ...x, _kind: "project",
                       _type: x.project_type === "self_study" ? "self_study" : "lecture",
                       _collection: "lecture" })),
    ];
    setProjects(out);
  }, [user?.uid]);

  useEffect(() => { afterViewTransition().then(loadProjects); }, [loadProjects]);

  const load = useCallback(async () => {
    if (!user?.uid) { setLoading(false); return; }
    try {
      const { page, done } = await fetchPage(0);
      offsetRef.current = page.length;
      setHasMore(!done);
      setVideos(page.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    } catch {
      setVideos([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, fetchPage]);

  /* THE HAND-OFF, on this page only. The bar starts where Home's composer was — its width and
     its y — and both changes begin on the same frame, but the width finishes within the first
     fifth of the travel. So it is the right shape almost immediately and then simply arrives,
     rather than resizing the whole way down. Waiting for the thin to finish before moving was
     an earlier attempt and it read as a stall at the top of the journey.
     useLayoutEffect, not useEffect: the starting geometry has to be on the element BEFORE the
     browser paints it, or the bar appears in its final place for a frame and then jumps back
     to animate. Both beats live in one transition declaration, so there are no timers to
     drift apart. */
  const dockRef = useRef(null);
  useLayoutEffect(() => {
    if (!isVisible) return;
    const from = takeComposerFrom();
    const el = dockRef.current;
    if (!from || !el) return;
    const rect = el.getBoundingClientRect();
    const dy = from.top - rect.top;
    if (Math.abs(dy) < 2 && Math.abs(from.width - rect.width) < 2) return;

    el.style.transition = "none";
    el.style.maxWidth = `${from.width}px`;
    el.style.transform = `translateY(${dy}px)`;

    // Two frames: one to commit the starting geometry untransitioned, one to animate off it.
    // A single rAF sometimes coalesces both into one style recalc and nothing animates at all.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      // No delay on either — they start together, and the curves do the shaping. See
      // composerHandoff for why the travel is eased at both ends and the width only at its end.
      el.style.transition =
        `max-width ${DOCK_THIN_MS}ms ${DOCK_THIN_EASE}, ` +
        `transform ${DOCK_SLIDE_MS}ms ${DOCK_SLIDE_EASE}`;
      el.style.maxWidth = "";        // back to the class's max-w-3xl
      el.style.transform = "translateY(0)";
    }));

    // Leave no inline styles behind: anything still set here would override the class the next
    // time the bar needs to be laid out normally (a resize, a mode switch).
    const clear = (e) => {
      if (e.propertyName !== "transform") return;
      el.style.transition = "";
      el.style.transform = "";
      el.removeEventListener("transitionend", clear);
    };
    el.addEventListener("transitionend", clear);
    return () => el.removeEventListener("transitionend", clear);
  }, [isVisible]);

  // ONE trigger yields at least one new DAY ROW. Rows are horizontal, so a page of 60
  // videos usually just extends a row that is already off-screen to the right — the page
  // gets no taller, the sentinel stays in view, and the observer fires again immediately.
  // That is what made it burst through eight pages at once and flicker. So a trigger keeps
  // pulling pages until a genuinely new day appears (or the server runs out), then stops.
  const loadingRef = useRef(false);
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore || !user?.uid) return;
    loadingRef.current = true;
    setLoadingMore(true);
    try {
      const seenDays = new Set(videos.map((v) => dayKey(v.created_at)));
      let added = [];
      let ended = false;
      for (let i = 0; i < 6; i++) {
        const { page, done } = await fetchPage(offsetRef.current);
        offsetRef.current += page.length;
        added = added.concat(page);
        if (done) { ended = true; break; }
        if (page.some((v) => !seenDays.has(dayKey(v.created_at)))) break;
      }
      setHasMore(!ended);
      // Dedupe by id: an offset window can overlap when something is created mid-scroll.
      setVideos((prev) => {
        const seen = new Set(prev.map((v) => v.video_id));
        const fresh = added.filter((v) => !seen.has(v.video_id));
        if (!fresh.length) return prev;
        return [...prev, ...fresh]
          .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      });
    } catch {
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, user?.uid, fetchPage, videos]);

  /* HELD UNTIL THE MORPH IS OVER. Arriving from Home runs a View Transition that animates the
     composer's position and size — main-thread work — and this fetch resolving mid-animation
     sets state for a whole grid of cards and kicks off thumbnail decoding right on top of it,
     which is what made the hand-off stutter. Nothing is delayed when there is no transition
     running: afterViewTransition() is an already-resolved promise then. */
  useEffect(() => { afterViewTransition().then(load); }, [load]);

  /* The composer is fixed to the bottom of the WINDOW. If the window itself extends past the
     bottom of the display — trivial to do on macOS by dragging, and it survives restarts —
     the browser still lays the bar out at the window's bottom edge, which is off-screen. Top
     anchored chrome looks fine, the bar looks dragged under the screen, and nothing in CSS or
     `innerHeight` can see it: to the page the viewport is simply taller than the monitor.

     So measure it. `screenY + outerHeight` is where the window bottom sits in screen
     coordinates; `availTop + availHeight` is where the usable screen ends. The difference is
     how much is hidden, and the bar lifts by exactly that. Zero whenever the window is fully
     on-screen, so this is inert in the normal case. Clamped, because a bogus reading that
     shoved the bar into the middle of the page would be worse than the bug. Polled as well as
     event-driven: moving a window fires no resize event. */
  useEffect(() => {
    if (!isVisible) return;
    const measure = () => {
      const screenBottom = (window.screen.availTop ?? 0) + window.screen.availHeight;
      const windowBottom = window.screenY + window.outerHeight;
      const hidden = Math.round(windowBottom - screenBottom);
      // 24px floor so rounding noise never nudges the bar; 400 ceiling so a bogus reading
      // cannot fling it into the middle of the page.
      const lift = hidden >= 24 && hidden < 400 ? hidden : 0;
      document.documentElement.style.setProperty("--magi-offscreen-bottom", `${lift}px`);
    };
    measure();
    window.addEventListener("resize", measure);
    const poll = setInterval(measure, 1000);
    return () => {
      window.removeEventListener("resize", measure);
      clearInterval(poll);
      document.documentElement.style.removeProperty("--magi-offscreen-bottom");
    };
  }, [isVisible]);

  // Rewind every day-row to its start whenever the Workspace is entered.
  //
  // Submitting from the home composer lands you here with a brand-new job, and new work is
  // prepended to the LEFT of its row — but the rows keep whatever horizontal scroll they had
  // from last visit, so the thing you just made could sit off-screen behind older cards. The
  // page then looks like the submission did nothing.
  //
  // Runs after paint (rAF) because the rows are re-rendered as `videos`/`jobs` resolve; a
  // synchronous reset would be undone by that render. `behavior: auto` is deliberate — an
  // animated scroll on arrival reads as the page moving on its own.
  useEffect(() => {
    if (!isVisible) return;
    const rewind = () => {
      document.querySelectorAll(".ws-row").forEach((row) => {
        if (row.scrollLeft !== 0) row.scrollTo({ left: 0, behavior: "auto" });
      });
    };
    const raf = requestAnimationFrame(rewind);
    return () => cancelAnimationFrame(raf);
  // NB deps: `jobs` is declared further down this component, so referencing it here would
  // be a temporal-dead-zone ReferenceError. The queue length is the same signal.
  }, [isVisible, videos, generationQueue?.queue?.length]);

  /* SEARCH GOES TO THE SERVER. It used to filter `videos` — the progressively-loaded page
     cache — which produced the worst bug on this page:

       filtering hides most rows -> the page becomes short -> the bottom sentinel is in view
       -> loadMore() fires -> it pulls up to SIX pages -> almost none match -> the page is
       still short -> the sentinel is still in view -> it fires again.

     So a search kicked off a chase through the entire library, 360 videos at a time, each
     batch re-rendering and inserting newer matches ABOVE what you were already reading. That
     is the twenty seconds of skeletons and the content appearing above the fold.

     It was also WRONG, not just slow: it could only ever match videos already fetched, so
     results depended on how far you happened to have scrolled.

     One request, server-ranked, no pager. */
  const [searchVideos, setSearchVideos] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!wsQuery || !user?.uid) { setSearchVideos(null); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    apiService.getUserContent(user.uid, "videos", 60, 0, wsQuery)
      .then((data) => {
        if (cancelled) return;
        setSearchVideos((data?.videos || []).filter((v) => v.video_id));
      })
      .catch(() => { if (!cancelled) setSearchVideos([]); })
      .finally(() => { if (!cancelled) setSearching(false); });
    return () => { cancelled = true; };
  }, [wsQuery, user?.uid]);

  // Pull the next page when the bottom comes into view. rootMargin fires it early so the
  // rows are already there by the time you reach them.
  useEffect(() => {
    const el = sentinelRef.current;
    // `wsQuery` guard: with a query active the list is server-supplied and finite, so there
    // is nothing to page — and paging is exactly what made searching thrash.
    if (!el || !isVisible || !hasMore || wsQuery) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
    // `loading` matters: the sentinel is not rendered during the first fetch, so without it
    // the effect runs once against a null ref and never re-runs to pick the node up.
  }, [isVisible, hasMore, loadMore, loading, wsQuery]);

  /* WHICH FAILED CARD IS MID-RETRY, so its button can say so and cannot be pressed twice.
     One id rather than a set: these cards sit in a row of at most a handful and a double
     press on the same one is the only mistake available. */
  const [retrying, setRetrying] = useState(null);

  /* RETRY A FAILED JOB. The server rebuilds it from the request it was stored with and
     returns a NEW job, which the queue picks up as an ordinary generating card — so the
     dead card is removed rather than left sitting beside its own replacement.

     ON FAILURE THE CARD STAYS. Removing it would delete the only evidence that anything
     went wrong, and the person would be looking at an empty row wondering what they just
     pressed. */
  const retryJob = useCallback(async (job) => {
    if (!job?.id || retrying) return;
    setRetrying(job.id);
    try {
      const res = await apiService.retryJob(job.id);
      if (res?.job_id) {
        /* `jobId`, not `id` — addJob reads jobData.jobId and subscribes to progress with
           it; passing `id` makes a card that never updates. */
        generationQueue?.addJob?.({ jobId: res.job_id, title: job.title,
                                    type: job.type || "video",
                                    metadata: job.metadata });
        generationQueue?.removeJob?.(job.id);
      }
    } catch (e) {
      console.error("retry failed", e);
    } finally {
      setRetrying(null);
    }
  }, [retrying, generationQueue]);

  // Anything still generating, newest first. Silent jobs are background work the user never
  // asked to watch, so they stay out of the grid.
  const jobsView = useMemo(() => {
    const q = generationQueue?.queue || [];
    const have = new Set(videos.map((v) => v.video_id));
    const haveSessions = new Set(projects.map((p) => p.session_id).filter(Boolean));
    /* WHAT REPLACES A FINISHED TILE DIFFERS BY KIND, and getting that wrong strands the tile.
     *
     * A single video's tile is held until that video appears in the grid. A PROJECT's tile
     * cannot use the same test: its `videoId` is whichever sub-video finished first, and a
     * project's videos are deliberately kept out of the grid now that they are folded into
     * their project card (see the member filter in `groups`). So the video it was waiting for
     * is one that will never arrive, and the held tile would sit next to the finished group
     * forever. What actually replaces a project's tile is the PROJECT CARD, so that is what
     * it waits for. */
    const held = (j) => {
      if (j.state !== "completed") return false;
      if (j.devEmpty) return true;   // produces no video, so nothing can replace it
      if (j.type === "project") {
        return !j.metadata?.sessionId || !haveSessions.has(j.metadata.sessionId);
      }
      return Boolean(j.videoId) && !have.has(j.videoId);
    };
    /* A FAILURE STAYS UNTIL IT IS DISMISSED. Failed jobs used to be absent from this list
       entirely, so a card that had been counting up for twenty minutes simply DISAPPEARED —
       no row, no message, nothing to click. It now stays where it was, says what happened,
       and leaves when the × on it is pressed (`removeJob`). */
    const list = q
      .filter((j) => !j.silent && (
        j.state === "queued" || j.state === "processing"
        || j.state === "failed" || held(j)
      ))
      .slice()
      .reverse();
    // `anyHeld` rides along rather than being computed again elsewhere: it is the termination
    // condition for the re-read loop below, and a second copy of `held` is how that loop and
    // the tile it is waiting on would come to disagree about what "waiting" means.
    return { list, anyHeld: list.some(held) };
  }, [generationQueue?.queue, videos, projects]);
  const jobs = jobsView.list;
  /* Whether anything is still waiting for the grid, readable from inside a timeout without
     making that timeout depend on the value (which would reschedule it on every render). */
  const heldRef = useRef(jobsView.anyHeld);
  useEffect(() => { heldRef.current = jobsView.anyHeld; }, [jobsView.anyHeld]);

  // A job leaving the active set means a real video now exists for it — re-read so the
  // placeholder is replaced by the thing itself rather than just vanishing.
  //
  // TWO READS, THE FIRST ONE SOON. The card is deliberately held on "ready" until its video
  // appears in the grid (see `jobs` above), so this fetch is the ONLY thing that ends that
  // state — and at a flat 2.5s delay plus the page fetch itself, a finished video sat there
  // announcing it was ready for the better part of ten seconds. The first read now goes out
  // at 700ms, which is usually enough. The second is the insurance: the write has to land in
  // Firestore and be visible to a query before it can come back in a page, and if the early
  // read misses it the card would otherwise stay held until something else triggered a load.
  const activeIds = jobs
    .filter((j) => j.state === "queued" || j.state === "processing")
    .map((j) => j.id).join(",");
  /* Something is being drawn RIGHT NOW, which is the only state the first-generation hint
     describes. Read off activeIds rather than filtering `jobs` a second time: `jobs` also
     carries work that has finished and is only being held until its video appears in the grid
     (see the memo above), and a bubble explaining the wait must not open over a tile that has
     stopped waiting. */
  const rendering = activeIds.length > 0;
  const prevActiveIds = useRef(activeIds);
  useEffect(() => {
    const before = new Set(prevActiveIds.current.split(",").filter(Boolean));
    const now = new Set(activeIds.split(",").filter(Boolean));
    prevActiveIds.current = activeIds;
    if ([...before].some((id) => !now.has(id))) {
      /* PROJECTS TOO, not just videos. `loadProjects` ran once on mount, and this page stays
         mounted for the life of the app — so after finishing a homework set the group card
         did not exist in `projects` and could not appear until something forced a full
         reload. Meanwhile its tile was held waiting for that very card. Re-reading both here
         is what closes the loop: the project lands, the tile is released, and the card takes
         its place. */
      /* RETRY UNTIL THE TILE IS ACTUALLY RELEASED, not twice on a timer.
       *
       * It used to be exactly two reads, at 700ms and 3500ms, and the comment above admitted
       * the hole: "if the early read misses it the card would otherwise stay held until
       * something else triggered a load." That is a race against Firestore making the new row
       * visible to a QUERY, and when both reads lose it the tile sits there looking like it is
       * still generating — for ever, because nothing retried. Reported as "it shows a video
       * loading but a new tab shows it completed": the new tab wins because mounting does a
       * fresh fetch.
       *
       * So the schedule keeps going until there is nothing left waiting. `heldRef` is what the
       * `jobs` memo computes as `held` — a tile that has finished and is waiting for its video
       * (or its project card) to appear in the grid — so "no held tiles" is the real
       * termination condition rather than a guess at how long Firestore takes.
       *
       * ONE MINUTE, THEN STOP. A tile still held after a minute is not losing a race any more,
       * it is a job whose video never landed; continuing to poll would hide that rather than
       * fix it, and an unbounded retry on a page that stays mounted for the life of the app is
       * how you get a background request loop nobody remembers writing.
       *
       * Backoff rather than a fixed interval: the first read almost always wins, so the common
       * case costs the same two requests it did before. */
      const DEADLINE_MS = 60 * 1000;
      // MARKS FROM THE MOMENT THE JOB FINISHED, not gaps. Each read is scheduled against real
      // elapsed time, so a tab suspended between two of them resumes on the right mark rather
      // than firing the backlog in a burst.
      const MARKS = [700, 1500, 3000, 5000, 8000, 12000, 18000, 25000, 35000, 50000];
      const started = Date.now();
      const timers = [];
      let stopped = false;

      const tick = (i) => {
        if (stopped) return;
        load();
        loadProjects();
        const next = MARKS[i + 1];
        if (next == null || next > DEADLINE_MS) return;
        timers.push(setTimeout(() => {
          // Released in the meantime — the read that did it was the whole point, so stop.
          if (stopped || !heldRef.current) return;
          tick(i + 1);
        }, Math.max(0, next - (Date.now() - started))));
      };
      timers.push(setTimeout(() => tick(0), MARKS[0]));
      return () => { stopped = true; timers.forEach(clearTimeout); };
    }
  }, [activeIds, load, loadProjects]);

  /* Thumbnails for a project's own lessons, so a Homework or Lecture card can show the
     videos it produced instead of a list of its problem text.

     A job id ALWAYS works, but it is the SLOW way, so prefer the loaded video when there is
     one. extractThumbnailUrl has a priority order: a thumbnail_storage_path or thumbnail_url
     on the object resolves to a direct Firebase Storage URL, while an object carrying only
     job_id falls through to `/api/media/thumbnail/<id>` on our own backend — which does a
     Firestore read and a 302 per image, and for any video whose thumbnail was never stored
     downloads the whole mp4 and runs ffmpeg inside the request. Measured on the Workspace
     page: thumbnail requests were p50 445ms, p90 6.0s, worst 13.7s, ~107 of them.
     Passing bare job_ids (as SessionView does, where there are only a handful) put every
     mosaic tile on that slow path. So: hand VideoThumbnail the real video object when the
     loaded page has it — direct storage URL, no backend hop — and fall back to the job_id
     shape when it does not, which is what keeps projects older than the current page working
     at all.

     Still-generating and failed jobs are dropped rather than drawn as black tiles, so a
     half-finished project shows the lessons it already has. */
  const videoByJobId = useMemo(() => {
    const m = new Map();
    videos.forEach((v) => {
      if (v.job_id) m.set(v.job_id, v);
      if (v.video_id) m.set(v.video_id, v);
    });
    return m;
  }, [videos]);

  const projectCovers = useCallback((p) => {
    const out = [];
    for (const j of Object.values(p.video_jobs || {})) {
      if (!j?.job_id || j.status === "generating" || j.status === "failed") continue;
      const loaded = videoByJobId.get(j.job_id) || videoByJobId.get(j.url);
      out.push(loaded || { job_id: j.job_id, video_type: "educational" });
      if (out.length === 4) break;
    }
    return out;
  }, [videoByJobId]);

  // One row per DAY, and only for days something was made — an empty day gets no row at all,
  // so the column is a record of when you worked rather than an evenly-spaced calendar.
  // Jobs still running belong to today, at the front: they are the most recent thing made.
  const groups = useMemo(() => {
    const order = [];
    const by = {};
    // Match on the title AND the prompt: the title is often a tidied version of what you
    // typed, so searching your own words would otherwise miss the video you were thinking of.
    const q = wsQuery.toLowerCase();
    /* A PROJECT'S VIDEOS BELONG TO THE PROJECT, NOT ALSO TO THE TIMELINE.
     *
     * `loadProjects` already states half of this rule — a lecture session whose project_type is
     * "video" is dropped because "its video ALREADY appears in the timeline from video_history".
     * The other half was never written. A homework set of two problems produced a project card
     * AND both of its solution videos as loose cards: three entries in the day row for one
     * thing you made, with the group sitting among its own members looking like a fourth kind
     * of item. Two problems made it read as two separate videos rather than as one set.
     *
     * The link only exists on the SESSION — `video_history` rows carry no session id, so this
     * cannot be done by asking a video what it belongs to. Every id a session stores for a
     * generated video goes in: `job_id` and `video_id` are the same uuid on a freshly created
     * set, `url` is that uuid on some and a full URL on others. A full URL simply never matches
     * a video_id, so collecting all three costs nothing and survives the inconsistency.
     *
     * NOT APPLIED TO SEARCH. Browsing is about structure, so the same work should appear once;
     * searching is about finding one thing, and a problem video's text ("Prove Proposition
     * 1.18…") is nothing like its project's title ("MATH 425A Fall 2025 HW 2"). Hiding it from
     * results would make a video you can see in the project unfindable by what it actually says.
     */
    const memberIds = new Set();
    projects.forEach((p) => {
      Object.values(p.video_jobs || {}).forEach((j) => {
        [j?.job_id, j?.video_id, j?.url].forEach((id) => { if (id) memberIds.add(id); });
      });
    });

    // With a query the rows come from the server (see the searchVideos effect); the local
    // page cache is not consulted at all, so results no longer depend on how far you had
    // scrolled. `|| []` while the request is in flight, so the old results do not linger
    // under the new query.
    const source = q
      ? (searchVideos || [])
      : videos.filter((v) => !memberIds.has(v.video_id) && !memberIds.has(v.job_id));
    source.forEach((v) => {
      const k = dayKey(v.created_at);
      if (!by[k]) { by[k] = []; order.push(k); }
      by[k].push({ ...v, _kind: "video" });
    });

    // Projects join the SAME day rows. Filtered by the same query so search does not
    // silently leave them behind when it hides everything around them.
    /* ONE THING ON SCREEN PER THING YOU MADE — THE TILE, THEN THE CARD.
     *
     * `jobs` above holds a project's tile until its card is in the grid, which says the two are
     * meant to be alternatives. They were not: the session document is written the moment you
     * press Create, long before any lesson has rendered, so the card appeared immediately and
     * sat NEXT TO the tile that was tracking the very same work. Two entries in the day row for
     * one homework set, one of them a nameless black rectangle — reported as "the thumbnail on
     * one thing and the loading project next to it, as if they were two different things".
     *
     * So the card waits its turn. While a project's job is in flight the tile owns the slot;
     * when the job leaves, `held()` drops the tile on the same render this lets the card
     * through, so the handover has no frame with both and none with neither.
     *
     * NOT APPLIED TO SEARCH, for the same reason the member filter is not: a search is for
     * finding one thing, and hiding a project you can name because it happens to be rendering
     * would make it unfindable while it is the most interesting thing you own. */
    const generating = new Set(
      jobs.filter((j) => j.type === "project" && j.metadata?.sessionId)
          .map((j) => j.metadata.sessionId)
    );
    const projSource = q
      ? projects.filter((p) => `${p.title || ""} ${p.topic || ""}`.toLowerCase().includes(q))
      : projects.filter((p) => !generating.has(p.session_id));
    projSource.forEach((p) => {
      const k = dayKey(p.created_at);
      if (!by[k]) { by[k] = []; order.push(k); }
      by[k].push(p);
    });

    // Re-sort every day newest-first: videos and projects arrive from two separate fetches,
    // so appending projects after videos would put an evening project before a morning
    // video within the same row.
    Object.values(by).forEach((arr) =>
      arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)));
    // Sort days by DATE, not by the key string: dayKey is `YYYY-M-D` with a 0-indexed,
    // un-padded month and day, so "2026-7-5" sorts above "2026-7-27" lexically and
    // November lands before July. Each day is already newest-first, so its [0] is its
    // newest item and comparing those orders the days correctly.
    order.sort((a, b) =>
      new Date(by[b][0].created_at || 0) - new Date(by[a][0].created_at || 0));

    const out = order.map((k) => ({
      key: k, label: dayLabel(by[k][0].created_at), items: by[k], jobs: [],
    }));
    if (!jobs.length || q) return out;   // a filtered view shows matches only

    const todayKey = dayKey(new Date().toISOString());
    if (out[0]?.key === todayKey) out[0] = { ...out[0], jobs };
    else out.unshift({ key: todayKey, label: "Today", items: [], jobs });
    return out;
  }, [videos, projects, jobs, wsQuery, searchVideos]);


  /* `copyPrompt` stood here: never called, and calling `setCopied` — a setter this file does
     not have and never did, with no "Copied" affordance anywhere to drive. The throw landed
     inside a .then() whose .catch swallowed it, so it could sit here indefinitely looking like
     working code. Removed rather than wired up: there is nothing on this page that copies. */

  const isSubscriber =
    credits?.subscription_plan && credits.subscription_plan !== "free";
  // Both the href and the click handler read this. A card whose right-click opens a
  // different page than its left-click is worse than having no href at all.
  const projectHref = useCallback((p) => (
    p._collection === "homework"
      ? `/homework/session/${p.session_id}`
      : `/workspace/session/${p.session_id}`
  ), []);

  // Seeded with what we already hold so the player can start immediately instead of
  // re-fetching metadata it is being handed. Mirrors ExplorePage's handleVideoClick.
  const openVideo = useCallback((v) => {
    navigate(`/video/${v.video_id}`, {
      state: {
        videoId: v.video_id,
        videoUrl: v.download_url,
        title: v.title,
        previousPage: "workspace",
      },
    });
  }, [navigate]);

  const openProject = useCallback((p) => {
    // Mark read on open so the unread dot clears, exactly as the Study page did. Fire and
    // forget: a failed flag must never block navigation into the project.
    if (!p.opened) {
      const update = p._collection === "homework" ? updateHomeworkSession : updateLectureSession;
      update(p.session_id, { opened: true }).catch(() => {});
      setProjects((prev) => prev.map((x) =>
        x.session_id === p.session_id ? { ...x, opened: true } : x));
    }
    navigate(projectHref(p));
  }, [navigate, projectHref]);

  const activeMode = MODES.find((m) => m.value === mode) || MODES[0];
  const isHandoff = activeMode.input === "handoff";

  const submit = useCallback(async () => {
    if (sending || !user?.uid) return;

    const text = prompt.trim();

    // Flow modes open the create modal in place, seeded with the typed text as the title or
    // first topic. Empty is fine — the modal asks for whatever it still needs.
    if (isHandoff) {
      setCreateSeed({ type: activeMode.project, topic: text });
      setPrompt("");
      return;
    }

    /* Same local-only short circuit as HomePage: the Empty mode never reaches the backend,
       so it has to be handled before the credit gate and the request below. The picker entry
       only exists in a dev build, so this is unreachable in production. */
    if (mode === DEV_EMPTY_MODE) {
      generationQueue?.addDevEmptyJob(text);
      setPrompt("");
      return;
    }
    if (mode === DEV_FAIL_MODE) {
      generationQueue?.addDevFailedJob(text);
      setPrompt("");
      return;
    }

    if (!text || !generateUnified) return;

    // Credit gate, mirroring HomePage.handleGenerate. Without it the docked composer queued
    // a job the server would refuse, so the user watched a card appear and then fail — the
    // Workspace was a way to spend credits you did not have.
    //
    // Anonymous visitors are deliberately NOT checked here: they have no users doc, so the
    // remaining count reads 0 and every anonymous generation would be blocked before the
    // request was sent. Their allowance is the server-side daily counter.
    // `!creditsLoading` matters: while the user doc is in flight `available` is 0, and
    // without this the very first submit after a page load would be refused for a user
    // who has plenty of credits.
    if (user && !user.isAnonymous && !creditsLoading && (credits?.available ?? 0) < 1) {
      window.dispatchEvent(new Event("magi:open-subscribe"));
      return;
    }

    setSending(true);
    try {
      await generateUnified(text, user.uid, { model: mode });
      setPrompt("");
      // The job goes to the queue, not video_history, so re-reading now shows nothing new.
      setTimeout(load, 4000);
    } catch {
      /* the queue surfaces its own failures */
    } finally {
      setSending(false);
    }
  }, [prompt, sending, user, credits, creditsLoading, generateUnified, mode, load, isHandoff,
      activeMode, navigate, generationQueue]);


  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* pb clears the docked composer so the last row is never trapped under it */}
      {/* pt-6 rather than pt-14: that padding existed to clear the `fixed top-3` search
          pill floating above the page. The field now sits in the header row below, so the
          gap it was reserving is dead space. */}
      <div className="max-w-[1400px] mx-auto px-6 pt-6 pb-56">
        {/* Header row. Navigation portals the desktop search field into the slot (see the
            magi-search-slot lookup there); on mobile the field renders inline in the nav,
            so this row collapses to just the heading. */}
        {/* min-h RESERVES THE SEARCH FIELD'S HEIGHT before it arrives.
            Navigation portals the field into the slot below, and it does so one frame late —
            a requestAnimationFrame that re-runs on every page switch. So this row first
            painted at the h1's height (~28px) and then grew to the input's (h-11 = 44px) a
            frame later, inserting ~16px ABOVE every row on the page and shoving the whole
            list down. That shift is what read as the Workspace scrolling up a bit each time
            you switched to it — not the scroll reset, which is instant (scroll-behavior is
            auto). Reserving the taller of the two heights means nothing moves when the field
            lands. */}
        <div className="hidden md:flex items-center gap-4 mb-6 min-h-[44px]">
          <h1 className="text-xl font-bold text-gray-900 shrink-0"
              style={{ fontFamily: ROW_TITLE_FONT }}>
            {t("nav_workspace")}
          </h1>
          <div id="magi-search-slot-workspace" className="flex-1 min-w-0 max-w-lg ml-auto" />
        </div>

        {loading && (
          <div className={phone ? "flex gap-[6px] overflow-hidden" : "flex gap-4"}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={phone ? { width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 } : { width: CARD_W, height: ROW_H }}
                className={`shrink-0 rounded-xl bg-gray-100 ring-1 ring-gray-200 ${
                  phone ? "aspect-video" : ""
                }`}
              />
            ))}
          </div>
        )}

        {/* TWO DIFFERENT EMPTIES, and they were the same one.
            This block used to test only the BROWSE data — videos/projects/jobs — so during a
            search it said "Nothing here yet. Every video you make will appear here" even
            though the library is full and the query simply matched nothing. Worse, it did not
            exclude `searching`, so it rendered on top of the loading placeholders: an empty
            state and a loading state claiming the page at the same time.
            With a query the only thing that means "empty" is the RESULT SET. */}
        {!loading && !searching && (wsQuery
          ? groups.length === 0 && (
              <div className="py-24 text-center">
                <Film className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-1">
                  {t("search_no_results_for", { query: wsQuery })}
                </p>
              </div>
            )
          : videos.length === 0 && projects.length === 0 && jobs.length === 0 && (
              <div className="py-24 text-center">
                <Film className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 mb-1">{t("ws_empty_title")}</p>
                <p className="text-sm text-gray-400">{t("ws_empty_body")}</p>
              </div>
            )
        )}

        {/* SEARCH RENDERS IN ONE WAVE, OR NOT AT ALL.
            A query has two sources with very different latencies: projects are filtered
            locally and resolve instantly, videos come back from the server ~1s later. Letting
            both render as they arrive meant the matching PROJECTS appeared first and the video
            rows then sorted in ABOVE them — results arriving out of chronological order and
            visibly reshuffling, which is worse than waiting.
            So while the request is out, the day rows are suppressed entirely and only these
            placeholders show. Everything then appears at once, already in date order.
            Three rows rather than one: a single row left most of the page blank, which read as
            "no results" for the second it took. */}
        {wsQuery && searching
          ? Array.from({ length: 3 }).map((_, r) => (
              <section className="mb-10" key={`sk${r}`} aria-hidden="true">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3.5 h-3.5 rounded-full bg-slate-200 animate-pulse shrink-0" />
                  <span className="h-4 w-28 rounded bg-slate-200 animate-pulse" />
                  <span className="h-4 w-16 rounded bg-slate-200 animate-pulse" />
                </div>
                <div className="flex gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-slate-200 animate-pulse shrink-0"
                      style={phone ? { width: "calc((100% - 2 * 6px) / 3)", minWidth: 200, aspectRatio: "16 / 9" }
                                   : { width: CARD_W, height: ROW_H }}
                    />
                  ))}
                </div>
              </section>
            ))
          : groups.map(({ key, label: dayText, items, jobs: dayJobs }, rowIndex) => (
          <section key={key} className="mb-10">
            {/* Header line copied from the reference: ring marker, the day, a dot, then what
                was made — with the type on the far right. */}
            <div className="flex items-center gap-2 mb-3 text-sm">
              <span className="w-3.5 h-3.5 rounded-full border-[1.5px] border-gray-300 shrink-0" />
              <span className="text-gray-500 shrink-0">{dayText}</span>
              <span className="text-gray-300">•</span>
              <span className="text-gray-700 truncate">
                {items.length + dayJobs.length === 1
                  ? (displayTitle((items[0] || dayJobs[0]).title)
                     || items[0]?.prompt || t("ws_untitled"))
                  : `${items.length + dayJobs.length} ${t("ws_videos")}`}
              </span>
            </div>

            {/* A ROW. Overflows horizontally when a day was busy, rather than wrapping into a
                block — the row IS the day, and it should read as one. */}
            <RowGate eager={rowIndex < 2} phone={phone}>
            <DayRow total={items.length} phone={phone}>
              {(shown) => (
                <>
              {dayJobs.map((j) => {
                /* A GENERATING PROJECT ALREADY EXISTS, SO ITS COVER OPENS.
                   The session row is written before any video is queued (see
                   CreateProjectModal.handleCreate: createSession runs first, then the jobs),
                   and `updateProjectJob` puts its id on the queue entry a moment later. So
                   for the whole of the wait there IS a page to go to — the project, with a
                   card per problem and each one's own progress — and this tile was an inert
                   black square instead. Making the newest thing you made the one thing you
                   cannot open is the wrong way round.
                   NOT for a failed card: it carries its own Retry and dismiss buttons, and
                   buttons inside an anchor is both invalid markup and a click fight.
                   A real href, not an onClick, so cmd-click and "open in new tab" work —
                   same treatment as the finished project cards below. */
                const proj = j.type === "project" && j.state !== "failed" && j.metadata?.sessionId
                  ? { _collection: j.metadata.collection,
                      session_id: j.metadata.sessionId,
                      opened: true }
                  : null;
                const Tile = proj ? "a" : "div";
                return (
                <Tile
                  /* `uid`, not `id` — see useGenerationQueue.addJob. The id is swapped for the
                     real one a second after the card appears, and keying on it remounted this
                     tile at exactly that moment. */
                  key={j.uid || j.id}
                  {...(proj ? cardLinkProps(projectHref(proj), () => openProject(proj)) : {})}
                  /* What the first-generation bubble points at. Every generating tile carries
                     it and the bubble takes the first in document order, which — because
                     `jobs` is reversed and today's row is first — is the newest one. */
                  data-onboarding="generating"
                  /* The reason, available without taking a line of the card for it. */
                  title={j.state === "failed" ? (failureReasonFor(j.error) || undefined) : undefined}
                  style={phone ? { width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 } : { width: CARD_W, height: ROW_H }}
                  /* `relative`: ProgressDial's line is absolutely positioned against this
                     tile's bottom edge. */
                  className={`relative shrink-0 rounded-xl overflow-hidden bg-black ring-1 ring-gray-200 flex flex-col items-center justify-center gap-3 px-5 ${
                    phone ? "aspect-video" : ""
                  } ${proj ? "cursor-pointer hover:ring-[#635BFF] transition-colors" : ""}`}
                >
                  {/* A FAILED CARD SAYS SO, AND CARRIES ITS OWN DISMISS. No dial: a progress
                      indicator under the word "failed" is a contradiction, and the bar's
                      finished state looks like success. The × is the only way this card
                      leaves — see scheduleFailedDismiss for why nothing removes it on a
                      timer any more. */}
                  {j.state === "failed" ? (
                    <>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); generationQueue?.removeJob(j.id); }}
                        aria-label={t("ws_failed_dismiss")}
                        title={t("ws_failed_dismiss")}
                        className="absolute top-2 right-2 z-20 p-1 rounded-md text-white/70
                                   bg-black/50 hover:bg-black/80 hover:text-white
                                   focus:outline-none focus:ring-2 focus:ring-white/60"
                      >
                        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                      </button>
                      {/* ONE LINE. It was four — icon, heading, reason, refund — on a card
                          220px tall, which is a paragraph where a label belongs. The card's
                          whole job is to say WHICH of your videos died; the reason is on hover
                          (the title below) and the detail belongs in the banner, not here. */}
                      <AlertTriangle className="w-6 h-6 text-amber-400" aria-hidden="true" />
                      <p className="text-white text-sm font-bold text-center leading-snug">
                        {t("ws_failed_title")}
                      </p>
                      {/* THE ONE THING YOU ACTUALLY WANT FROM THIS CARD. A failure you can
                          see but not act on makes you go back to the box and retype a topic
                          you already typed; the server still has the exact request, so the
                          honest offer is "again", not "start over".

                          It REPLACES ITSELF with the new generating card: retrying removes
                          this job and the queue's new one takes its place, so the row never
                          shows the dead card beside its own retry. */}
                      <button
                        type="button"
                        disabled={retrying === j.id}
                        onClick={(e) => { e.stopPropagation(); retryJob(j); }}
                        className="mt-0.5 px-3 py-1 rounded-full text-[12px] font-semibold
                                   text-black bg-white hover:bg-gray-100 disabled:opacity-60
                                   disabled:cursor-default focus:outline-none focus:ring-2
                                   focus:ring-white/70 inline-flex items-center gap-1.5"
                      >
                        <RotateCcw className={`w-3 h-3 ${retrying === j.id ? "animate-spin" : ""}`}
                                   strokeWidth={2.5} aria-hidden="true" />
                        {retrying === j.id ? t("ws_failed_retrying") : t("ws_failed_retry")}
                      </button>
                    </>
                  ) : j.devEmpty && j.state === "completed" ? null : <ProgressDial job={j} />}
                </Tile>
                );
              })}
              {items.slice(0, shown).map((v) => v._kind === "project" ? (
                <a
                  key={v.session_id}
                  {...cardLinkProps(projectHref(v), () => openProject(v))}
                  style={phone ? { width: "calc((100% - 2 * 6px) / 3)", minWidth: 200 } : { width: CARD_W, height: ROW_H }}
                  className={`group relative shrink-0 rounded-xl overflow-hidden bg-black text-left border border-gray-200 hover:border-[#635BFF] transition-colors ${
                    phone ? "aspect-video" : ""
                  }`}
                >
                  {(() => {
                    const cfg = PROJECT_CONFIG[v._type] || PROJECT_CONFIG.lecture;
                    const Icon = cfg.Icon;
                    const items = (v._type === "hw" ? v.problems : v.topics) || [];
                    const count = items.length;
                    const covers = projectCovers(v);
                    /* The project's SIZE, not how much of it has rendered. video_jobs carries
                       one entry per selected problem/topic, so it counts the lessons the
                       project promises even while some are still generating — which is what
                       "3 lessons" means on an Explore course too. Falls back to the item list
                       for a session written before video_jobs existed. */
                    const lessonCount = Object.keys(v.video_jobs || {}).length || count;
                    return (
                      <>
                        {/* Built to the SAME recipe as the lesson card beside it: art edge to
                            edge, one scrim, bold white title over the bottom. It used to be a
                            dark cover stacked on a WHITE footer, which made every project read
                            as a hole punched in an otherwise all-dark row — the one thing the
                            cover was supposed to stop. The art here is the project's contents
                            rather than a rendered frame, and it falls back to the type icon
                            when the items have not loaded or the project is empty. */}
                        <div className="absolute inset-0" style={{ background: "#0F1115" }}>
                          {covers.length > 0 ? (
                            /* THE LESSONS IT MADE, laid out exactly like a course cover on
                               Explore — same 2x2 grid, same 2px gutters, same rule that with
                               three tiles the first spans both rows and with two they split
                               down the middle, so the mosaic always fills the frame instead of
                               leaving a hole. A project and a course are the same idea to a
                               user (a set of lessons under one title) and were the only two
                               things on the page drawn differently.
                               Falls through to the problem-text preview below when there are
                               no thumbnails yet, which is every project between creation and
                               its first finished render — that preview is now the EMPTY state
                               rather than the default. */
                            <div
                              className="grid w-full h-full gap-[2px]"
                              style={{ gridTemplateColumns: "repeat(2, 1fr)", gridTemplateRows: "repeat(2, 1fr)" }}
                            >
                              {covers.map((cv, i) => (
                                <div
                                  key={cv.video_id || cv.job_id || i}
                                  className="relative w-full h-full overflow-hidden"
                                  style={
                                    (covers.length === 3 && i === 0) || covers.length === 1 || covers.length === 2
                                      ? { gridRow: "span 2", ...(covers.length === 1 ? { gridColumn: "span 2" } : null) }
                                      : undefined
                                  }
                                >
                                  {/* lazy, not the component's eager default — this is an
                                      unbounded list of day rows, which is the exact case its
                                      own comment says to opt out for. */}
                                  <VideoThumbnail video={cv} alt="" loading="lazy" />
                                </div>
                              ))}
                            </div>
                          ) : items.length > 0 ? (
                            /* Top-padded clear of the type pill, bottom-padded clear of the
                               title, so neither ever lands on top of the preview lines. */
                            <div className="absolute inset-0 px-3.5 pt-10 pb-14 flex flex-col justify-center gap-1.5 overflow-hidden">
                              {items.slice(0, 3).map((it, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  {/* Plain numerals, not filled chips. A saturated block behind
                                      each digit turned the cover into a row of stickers. */}
                                  <span className="mt-[1px] w-4 flex-shrink-0 text-[11px] font-semibold text-white/30 tabular-nums text-right">
                                    {i + 1}
                                  </span>
                                  {/* Rendered, not raw. previewLabel used to STRIP the $…$
                                      delimiters, which left bare TeX commands as literal text —
                                      "\vec{a}" and "3 \times 10^8" on the face of the card. */}
                                  <span
                                    className="text-[11.5px] leading-snug text-white/70 line-clamp-2 [&_.katex]:text-[0.95em]"
                                    dangerouslySetInnerHTML={{ __html: safeRenderLatex(previewLabel(it)) }}
                                  />
                                </div>
                              ))}
                              {items.length > 3 && (
                                <span className="text-[11px] text-white/25 pl-6">
                                  +{items.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="absolute inset-0 grid place-items-center">
                              <Icon className="w-10 h-10" style={{ color: cfg.fg }} />
                            </div>
                          )}
                        </div>

                        {/* Type pill, ON the art. The old white strip spent a whole band of the
                            card restating what this one chip says. */}
                        {/* SOLID BLACK, not a tint of the type colour. This was `${cfg.fg}26`
                            — 15% alpha — which was fine over the old flat #0F1115 cover and
                            became unreadable the moment a mosaic went behind it: every lesson
                            thumbnail has its own title burned into the art, usually top-left,
                            exactly where this sits, so "Lecture" was printing on top of "TO
                            TAIL". An opaque chip is the only thing that survives arbitrary
                            artwork underneath. The type colour stays on the icon and label,
                            so the colour coding is unchanged. */}
                        <span
                          className="absolute top-2.5 left-2.5 z-30 inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-[3px] text-[10.5px] font-bold tracking-wide"
                          style={{ background: "#000", color: cfg.fg }}
                        >
                          <Icon className="w-3 h-3" />
                          {cfg.label}
                        </span>

                        {/* Top-right cluster: unread dot, then the lesson-count badge. These
                            were separate absolutes at top-3.5/right-3 and top-2/right-2 and
                            would have overlapped once the badge arrived; one flex row keeps
                            them apart whichever is showing.
                            The badge is the Explore course badge, same icon, same shape — a
                            project and a course are the same object to a user, so the thing
                            that says "this is a set of N lessons" should be identical. */}
                        <div className="absolute top-2 right-2 z-30 flex items-center gap-1.5">
                          {/* Unread marker, same meaning it had on the Study page. */}
                          {!v.opened && (
                            <span className="w-1.5 h-1.5 rounded-full bg-[#635BFF]" />
                          )}
                          {lessonCount > 0 && (
                            /* Opaque for the same reason as the type chip, and so the two
                               chips in this row read as one set rather than one solid and one
                               washed out. Explore's badge is bg-black/70; over a course cover
                               that is fine, over these it is not. */
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-black text-white text-[11px] font-semibold">
                              <Layers className="w-3 h-3" />
                              {lessonCount}
                            </span>
                          )}
                        </div>

                        <div
                          className="absolute inset-0 z-20 pointer-events-none"
                          style={{ background: TITLE_SCRIM }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 z-30 p-2.5 pointer-events-none">
                          <p
                            className="text-white text-sm sm:text-base font-bold leading-snug drop-shadow [&_.katex]:text-[0.9em]"
                            style={{ fontFamily: ROW_TITLE_FONT, ...CLAMP_2_LINES }}
                            dangerouslySetInnerHTML={{
                              __html: safeRenderLatex(
                                displayTitle(v.title) || v.topic || t("ws_untitled")),
                            }}
                          />
                          {lessonCount > 0 && (
                            <p className="text-white/70 text-xs mt-0.5">
                              {t("course_lesson_count", { n: lessonCount })}
                            </p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </a>
              ) : (
                <WsVideoCard
                  key={v.video_id}
                  v={v}
                  href={`/video/${v.video_id}`}
                  onOpen={() => openVideo(v)}
                  t={t}
                  phone={phone}
                />
              ))}
                </>
              )}
            </DayRow>
            </RowGate>
          </section>
        ))}

        {/* Infinite-scroll sentinel + shells. The skeleton mirrors a real day section —
            header line then a row of cards — so the layout does not jump when it resolves.
            No spinner. */}
        {hasMore && !loading && (
          <div ref={sentinelRef}>
            {/* Never while searching: the pager is disabled then, so these placeholders
                would sit there promising rows that are never coming. */}
            {loadingMore && !wsQuery && (
              <section className="mb-10">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-3.5 h-3.5 rounded-full bg-gray-200 shrink-0" />
                  <span className="h-3.5 w-24 rounded bg-gray-200" />
                  <span className="h-3.5 w-16 rounded bg-gray-100" />
                </div>
                <div className="flex gap-4">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{ width: CARD_W, height: ROW_H, animationDelay: `${i * 90}ms` }}
                      className="shrink-0 rounded-xl bg-gray-200 ring-1 ring-gray-200 ws-shell"
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* The first-generation hint, pointing at the tile that is rendering. `isVisible` is not
          optional: this page stays mounted while you are on Home (see App.jsx), and an overlay
          that spotlights one element and dims everything else has no business opening behind
          another page. */}
      {isVisible && <GeneratingBubble active={rendering} />}

      {/* ── Docked composer ─────────────────────────────────────────────────── */}
      {isVisible && !createSeed && createPortal(
        <div
          className="fixed right-0 z-40 pointer-events-none"
          style={{
            left: "var(--magi-sidebar, 0px)",
            // + the mobile tab bar's height, which is 0 at md and up. Docked at a flat 0 the
            // bar sat UNDERNEATH that nav (z-40 vs z-50) and lost its whole controls row.
            bottom: "calc(var(--magi-offscreen-bottom, 0px) + var(--magi-bottom-nav, 0px))",
          }}
        >
          <div ref={dockRef} className="max-w-3xl mx-auto px-6 pb-3.5 pointer-events-auto">
            {/* Arriving from Home, this bar plays the hand-off out of the hero composer's
                geometry: thin first, then travel. See the layout effect above. Nothing happens
                on a direct visit — there is no handed-off geometry to read. */}
            <PromptComposer
              value={prompt}
              onChange={setPrompt}
              onSubmit={submit}
              mode={mode}
              onModeChange={(v) => setMode(v)}
              onLockedMode={() => navigate("/credits")}
              /* A DROP IS NOW THE ONLY ROUTE TO THE PICKER. Same seed the `?mode=upload` effect
                 builds: files, whatever is typed as the title, and `upload: true` with NO type —
                 which is exactly the state CreateProjectModal reads as "starting from a file,
                 ask which kind". The menu no longer produces that state; Homework PDF and
                 Lecture PDF each carry their type (see composerConfig.js), so they skip the
                 chooser. A dropped file has said nothing about itself, so it still asks. */
              onFilesDropped={(files) => {
                setCreateSeed({ files, topic: prompt.trim(), upload: true });
                setPrompt("");
              }}
              onHandoff={(m) => {
                // Homework / Lecture / Project are LAUNCHERS, not modes. Picking one used to
                // switch the composer into a state whose only visible control was a text box
                // labelled "Name the assignment… (optional)" — an OPTIONAL field standing in
                // front of the required step, with nothing saying that Enter was what opened
                // the uploader. A bare ⬆ in a text box means "send this text"; it cannot mean
                // "open a file picker". So open the picker directly.
                //
                // Nothing is lost by skipping the name: CreateProjectModal has its own title
                // field, and if you leave it blank it names the project after the file.
                setCreateSeed({ type: m.project, topic: prompt.trim(), upload: m.upload });
                setPrompt("");
              }}
              isSubscriber={isSubscriber}
              sending={sending}
              placeholder={t(activeMode.input === "handoff"
                ? `ws_placeholder_${activeMode.project}`
                : "ws_composer_placeholder")}
              /* No view-transition-name any more: the hand-off is the two-stage shrink-then-
                 slide above, not a morph. Left as a comment rather than silence because the
                 name creating a stacking context is why the mode menu's z-index needed an
                 ancestor to sit on — see the z-30 note on Home. */
            />
          </div>
        </div>,
        document.body,
      )}

      {isVisible && createSeed && (
        <CreateProjectModal
          docked
          initialType={createSeed.type}
          initialTopic={createSeed.topic}
          initialFiles={createSeed.files}
          uploadOnly={createSeed.upload}
          generationQueue={generationQueue}
          onClose={() => setCreateSeed(null)}
          onCreated={(session, type) => {
            setCreateSeed(null);
            // Mark it opened the same way the Study page does, so the project does not show
            // up there as unread, then go straight into the session that was just created.
            const update = type === "hw" ? updateHomeworkSession : updateLectureSession;
            update(session.session_id, { opened: true }).catch(() => {});
            /* THE ROUTE HAS TO MATCH THE COLLECTION THE SESSION WAS WRITTEN TO.
             *
             * The line above branches on `type` and the navigation did not: a homework set was
             * created in the `homework` collection and then opened at /workspace/session/<id>,
             * which is the LECTURE page. It asks for GET /api/lecture/sessions/<id>, that 404s
             * because the session is not a lecture, and SessionView's failed-load path bounces
             * straight back to the Workspace — so pressing Create on a homework set threw you
             * out of the page you had just been sent to. Confirmed in the production log:
             * "Homework session created: 4c323be5-…" followed by
             * "GET /api/lecture/sessions/4c323be5-… -> 404", the same id, seconds apart.
             *
             * `video` is deliberately not a case here: that type writes a LECTURE session (with
             * project_type "video") and has always opened on it, which is correct for the route
             * even though the thing you are waiting for is a single video. */
            navigate(type === "hw"
              ? `/homework/session/${session.session_id}`
              : `/workspace/session/${session.session_id}`);
          }}
        />
      )}

    </div>
  );
};

export default WorkspacePage;
